import React, {ReactElement, useContext, useEffect, useRef, useState} from 'react';
import ToolbarNotificationService, {
  ToolbarNotificationShape,
} from '../../services/ToolbarNotificationService';
import {useBoolean} from '../../hooks/useBoolean';
import ToolbarNotificationDisplay from './toolbar-notification-display';
import {
  dismissToolbarNotification,
  showToolbarNotification,
} from '../../redux/toolbar-notification/actions';
import {useDispatch, useSelector} from 'react-redux';
import {Store} from '../../types/store';
import {IonToolbar} from '@ionic/react';
import {PlatformContext} from '../PlatformContext';
import styles from './styles.module.scss';

type DisplayableToolbarNotification = Pick<ToolbarNotificationShape, 'htmlContent'> & {
  _id?: string;
};

export default function ToolbarNotification(): ReactElement | null {
  const notificationWasDismissed = useBoolean(false);
  const dispatch = useDispatch();
  const toolbarNotificationRef = useRef<HTMLDivElement>(null);
  const authenticated = useSelector((state: Store) => state.authenticated);
  const platformContext = useContext(PlatformContext);

  const [notification, setNotification] = useState<DisplayableToolbarNotification | null>(null);
  const fetchCurrentNotification = async (): Promise<void> => {
    setNotification(await ToolbarNotificationService.showCurrent());
  };

  // Initial Fetch of Toolbar Notification
  useEffect(() => {
    if (!authenticated) return;

    fetchCurrentNotification();
  }, []);

  // re-fetch the current toolbar when one is dismissed
  useEffect(() => {
    if (notificationWasDismissed.value) {
      fetchCurrentNotification();
      notificationWasDismissed.setFalse();
    }
  }, [notificationWasDismissed]);

  useEffect(() => {
    if (notification) {
      dispatch(showToolbarNotification(toolbarNotificationRef));
    } else {
      dispatch(dismissToolbarNotification());
    }
  }, [notification]);

  async function dismissNotification(): Promise<void> {
    if (!notification?._id) return;

    await ToolbarNotificationService.dismiss(notification._id);
    setNotification(null);
    notificationWasDismissed.setTrue();
  }

  if (!notification || !notification.htmlContent) {
    return null;
  }

  const notificationDisplay = (
    <div ref={toolbarNotificationRef}>
      <ToolbarNotificationDisplay
        htmlContent={notification.htmlContent}
        onDismiss={dismissNotification}
      />
    </div>
  );

  if (platformContext.isIonic) {
    return <IonToolbar className={styles.mobileToolbarWrapper}>{notificationDisplay}</IonToolbar>;
  }

  return notificationDisplay;
}
